import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { CheckCircle, Clock, Sparkle, X, type Icon as PhosphorIcon } from '@phosphor-icons/react';
import { Icon } from '../Icon';

/**
 * Figma P01 toasts: one dark surface, and only the icon carries the tone.
 * `success` green tick, `warning` amber clock (rate limit / wait), `info` amber sparkle ("Coming soon"), `error` red cross.
 */
export type ToastTone = 'success' | 'warning' | 'info' | 'error';

export interface ToastInput {
  title: string;
  description?: string;
  tone?: ToastTone;
  /** "Undo" only when the action really can be undone; "Retry" on a failure. Clicking it closes the toast. */
  action?: { label: string; onClick: () => void };
}

interface ToastItem extends ToastInput {
  id: number;
}

const TOAST_MS = 4000;

const ICON: Record<ToastTone, { icon: PhosphorIcon; colour: string }> = {
  success: { icon: CheckCircle, colour: 'text-toast-success' },
  warning: { icon: Clock, colour: 'text-toast-warning' },
  info: { icon: Sparkle, colour: 'text-toast-warning' },
  error: { icon: X, colour: 'text-toast-error' },
};

const ToastContext = createContext<((toast: ToastInput) => void) | null>(null);

/** Mount once in the root layout. Shows at the bottom right for 4 s; hovering a toast holds it open. */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const nextId = useRef(0);
  const timers = useRef(new Map<number, ReturnType<typeof setTimeout>>());

  const dismiss = useCallback((id: number) => {
    clearTimeout(timers.current.get(id));
    timers.current.delete(id);
    setItems((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const arm = useCallback(
    (id: number) => {
      clearTimeout(timers.current.get(id));
      timers.current.set(
        id,
        setTimeout(() => dismiss(id), TOAST_MS),
      );
    },
    [dismiss],
  );

  const toast = useCallback(
    (input: ToastInput) => {
      const id = nextId.current++;
      setItems((prev) => [...prev, { ...input, id }]);
      arm(id);
    },
    [arm],
  );

  useEffect(() => {
    const pending = timers.current;
    return () => pending.forEach(clearTimeout);
  }, []);

  const value = useMemo(() => toast, [toast]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div
        aria-live="polite"
        className="pointer-events-none fixed inset-x-0 bottom-(--mbm-space-6) z-(--mbm-z-toast) flex flex-col items-end gap-(--mbm-space-2) px-(--mbm-space-6)"
      >
        {items.map((t) => {
          const { icon, colour } = ICON[t.tone ?? 'success'];
          return (
            <div
              key={t.id}
              role={t.tone === 'error' ? 'alert' : 'status'}
              onMouseEnter={() => clearTimeout(timers.current.get(t.id))}
              onMouseLeave={() => arm(t.id)}
              className="pointer-events-auto flex w-full max-w-(--mbm-layout-toast-width) items-center gap-(--mbm-space-3) rounded-(--mbm-component-radius-toast) bg-toast-surface p-(--mbm-space-3-5) text-toast-text shadow-(--mbm-shadow-table-card)"
            >
              <span className={`shrink-0 ${colour}`}>
                <Icon icon={icon} size="lg" />
              </span>
              <div className="flex min-w-0 flex-1 flex-col gap-(--mbm-space-0-5)">
                <p className="text-label-medium">{t.title}</p>
                {t.description ? <p className="text-body-small opacity-70">{t.description}</p> : null}
              </div>
              {t.action ? (
                <button
                  type="button"
                  onClick={() => {
                    t.action?.onClick();
                    dismiss(t.id);
                  }}
                  className="shrink-0 rounded-(--mbm-radius-xs) text-label-medium focus-visible:outline-none focus-visible:shadow-(--mbm-shadow-focus-ring)"
                >
                  {t.action.label}
                </button>
              ) : null}
            </div>
          );
        })}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const toast = useContext(ToastContext);
  if (!toast) throw new Error('useToast must be used inside <ToastProvider>');
  return toast;
}
